import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import type { Profile, GeneratedImage, Theme, GalleryImage } from '@/types';

// Profile operations
export const profileOperations = {
  async getProfile(userId: string): Promise<Profile | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (error) {
        logger.error('Error fetching profile', error, { operation: 'getProfile', userId });
        return null;
      }
      
      logger.debug('Profile fetched successfully', { operation: 'getProfile', userId });
      return data;
    } catch (error) {
      logger.error('Exception in getProfile', error, { operation: 'getProfile', userId });
      return null;
    }
  },

  async updateProfile(userId: string, updates: Partial<Profile>): Promise<Profile | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('user_id', userId)
        .select()
        .single();
      
      if (error) {
        logger.error('Error updating profile', error, { operation: 'updateProfile', userId, updates });
        return null;
      }
      
      logger.debug('Profile updated successfully', { operation: 'updateProfile', userId });
      return data;
    } catch (error) {
      logger.error('Exception in updateProfile', error, { operation: 'updateProfile', userId });
      return null;
    }
  }
};

// Generated images operations
export const imageOperations = {
  async getUserImages(userId: string): Promise<GeneratedImage[]> {
    const { data, error } = await supabase
      .from('generated_images')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching user images:', error);
      return [];
    }
    
    return data || [];
  },

  async getUserImageCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from('generated_images')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);
    
    if (error) {
      console.error('Error fetching user image count:', error);
      return 0;
    }
    
    return count || 0;
  },

  async getUserECardCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from('generated_images')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .like('prompt', 'Create a black-and-white coloring page e-card design for%');
    
    if (error) {
      console.error('Error fetching user e-card count:', error);
      return 0;
    }
    
    return count || 0;
  },

  async getUserColoringPageCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from('generated_images')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .not('prompt', 'like', 'Create a black-and-white coloring page e-card design for%');
    
    if (error) {
      console.error('Error fetching user coloring page count:', error);
      return 0;
    }
    
    return count || 0;
  },

  async createImage(imageData: Omit<GeneratedImage, 'id' | 'created_at'>): Promise<GeneratedImage | null> {
    const { data, error } = await supabase
      .from('generated_images')
      .insert([imageData])
      .select()
      .single();
    
    if (error) {
      console.error('Error creating image:', error);
      return null;
    }
    
    return data;
  },

  async deleteImage(imageId: string): Promise<boolean> {
    const { error } = await supabase
      .from('generated_images')
      .delete()
      .eq('id', imageId);
    
    if (error) {
      console.error('Error deleting image:', error);
      return false;
    }
    
    return true;
  }
};

// Theme operations
export const themeOperations = {
  async getAllThemes(): Promise<Theme[]> {
    const { data, error } = await supabase
      .from('themes')
      .select('*')
      .order('theme_name', { ascending: true });
    
    if (error) {
      console.error('Error fetching themes:', error);
      return [];
    }
    
    return data || [];
  },

  async getThemeById(themeId: string): Promise<Theme | null> {
    const { data, error } = await supabase
      .from('themes')
      .select('*')
      .eq('id', themeId)
      .single();
    
    if (error) {
      console.error('Error fetching theme:', error);
      return null;
    }
    
    return data;
  }
};

// Gallery images operations
export const galleryOperations = {
  async getAllGalleryImages(): Promise<GalleryImage[]> {
    const { data, error } = await supabase
      .from('gallery_images')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching gallery images:', error);
      return [];
    }
    
    return data || [];
  },

  async getGalleryImagesByTheme(theme: string): Promise<GalleryImage[]> {
    const { data, error } = await supabase
      .from('gallery_images')
      .select('*')
      .eq('is_active', true)
      .eq('theme', theme)
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching gallery images by theme:', error);
      return [];
    }
    
    return data || [];
  },

  async incrementLikes(imageId: string): Promise<boolean> {
    // First get current value, then increment (keep original logic for now)
    const { data: currentData, error: fetchError } = await supabase
      .from('gallery_images')
      .select('likes')
      .eq('id', imageId)
      .single();
    
    if (fetchError || !currentData) {
      console.error('Error fetching current likes:', fetchError);
      return false;
    }
    
    const { error } = await supabase
      .from('gallery_images')
      .update({ likes: currentData.likes + 1 })
      .eq('id', imageId);
    
    if (error) {
      console.error('Error incrementing likes:', error);
      return false;
    }
    
    return true;
  },

  async incrementDownloads(imageId: string): Promise<boolean> {
    // First get current value, then increment (keep original logic for now)
    const { data: currentData, error: fetchError } = await supabase
      .from('gallery_images')
      .select('downloads')
      .eq('id', imageId)
      .single();
    
    if (fetchError || !currentData) {
      console.error('Error fetching current downloads:', fetchError);
      return false;
    }
    
    const { error } = await supabase
      .from('gallery_images')
      .update({ downloads: currentData.downloads + 1 })
      .eq('id', imageId);
    
    if (error) {
      console.error('Error incrementing downloads:', error);
      return false;
    }
    
    return true;
  }
};